import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getHome } from '../api';
import { useAuth } from '../auth';
import { fmtDate, useT } from '../i18n';
import PageHeader from '../components/PageHeader';
import MatchLine from '../components/MatchLine';

export default function Home() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const q = useQuery({ queryKey: ['home'], queryFn: getHome });
  const d = q.data;
  const statusKey = d?.next?.myStatus === 'in' ? 'home.signedup' : d?.next?.myStatus === 'waitlist' ? 'home.waitlisted' : 'home.notsigned';

  return (
    <>
      <PageHeader eyebrow="BB Commercial" title={t('home.hi', { name: user?.displayName ?? '' })} subtitle={t('home.sub')} />
      {q.error && <div className="text-danger text-sm mb-4">{(q.error as Error).message}</div>}
      <div className="grid gap-4 sm:grid-cols-2">
        <section className="card p-5">
          <div className="text-xs uppercase tracking-widest text-dim">{t('home.next')}</div>
          {d?.next ? (
            <Link to={`/tournaments/${d.next.id}`} className="block mt-2">
              <div className="text-lg font-semibold">{d.next.name}</div>
              <div className="text-sm text-dim">{fmtDate(d.next.startsAt, lang)}{d.next.venue ? ` · ${d.next.venue}` : ''}</div>
              <div className="mt-2 flex gap-2">
                <span className={`pill ${d.next.status === 'live' ? 'bg-ball-500/15 text-ball-300' : 'bg-court-500/15 text-court-400'}`}>{t(d.next.status === 'live' ? 't.status.live' : 't.status.open')}</span>
                <span className="pill bg-ink-800 text-dim">{t(statusKey)}</span>
              </div>
            </Link>
          ) : <p className="mt-2 text-sm text-dim/80">{t('home.next.none')}</p>}
        </section>
        <section className="card p-5">
          <div className="text-xs uppercase tracking-widest text-dim">{t('home.me')}</div>
          {d && (
            <div className="grid grid-cols-4 gap-2 mt-3">
              <Stat label={t('home.rating')} value={d.me.rating} />
              <Stat label={t('home.rank')} value={d.me.rank ?? '–'} />
              <Stat label={t('home.handicap')} value={d.me.handicap > 0 ? `+${d.me.handicap}` : d.me.handicap} />
              <Stat label={t('home.matches')} value={d.me.matches} />
            </div>
          )}
        </section>
        <section className="card p-5">
          <div className="text-xs uppercase tracking-widest text-dim">{t('home.upcoming')}</div>
          {d && (d.upcoming.length ? (
            <ul className="mt-3 space-y-2">{d.upcoming.map((m) => <li key={m.id}><MatchLine m={m} names={d.names} me={user!.id} label={m.tournamentName} /></li>)}</ul>
          ) : <p className="mt-2 text-sm text-dim/80">{t('home.upcoming.none')}</p>)}
        </section>
        <section className="card p-5">
          <div className="text-xs uppercase tracking-widest text-dim">{t('home.recent')}</div>
          {d && (d.recent.length ? (
            <ul className="mt-3 space-y-2">{d.recent.map((m) => <li key={m.id}><MatchLine m={m} names={d.names} me={user!.id} label={m.tournamentName} /></li>)}</ul>
          ) : <p className="mt-2 text-sm text-dim/80">{t('home.recent.none')}</p>)}
        </section>
      </div>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return <div><div className="stat text-xl font-semibold">{value}</div><div className="text-[11px] text-dim uppercase tracking-wider">{label}</div></div>;
}
